import React from 'react';
import { Link } from 'react-router-dom';
import { Droplets, Mail, Phone, MapPin, Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const productLinks = [
    { path: '/products', label: 'Home Smart RO' },
    { path: '/products', label: 'Commercial RO' },
    { path: '/products', label: 'IoT Retrofit Kit' },
    { path: '/pricing', label: 'Pricing Plans' }
  ];

  const companyLinks = [
    { path: '/about', label: 'About Us' },
    { path: '/technology', label: 'Technology' },
    { path: '/use-cases', label: 'Use Cases' },
    { path: '/contact', label: 'Contact' }
  ];

  const supportLinks = [
    { path: '#', label: 'Help Center' },
    { path: '#', label: 'Installation Guide' },
    { path: '#', label: 'Warranty Info' },
    { path: '#', label: 'Service Request' }
  ];

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-6 group">
              <img 
                src="https://customer-assets.emergentagent.com/job_smart-ro-monitor/artifacts/3anjd9rc_Niloman-HX%20Logo.jpg"
                alt="Niloman HX Logo"
                className="h-14 w-auto group-hover:scale-105 transition-transform duration-300"
              />
              <span className="text-2xl font-bold text-white">
                Niloman HX
              </span>
            </Link>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Smart IoT-enabled water purification systems that bring transparency, reliability, and intelligence to your water quality monitoring.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <a href="tel:+919217401431" className="flex items-center gap-3 text-gray-400 hover:text-cyan-400 transition-colors">
                <Phone className="h-5 w-5" />
                <span>+91 9217401431</span>
              </a>
              <a href="mailto:info@nilomanhx.com" className="flex items-center gap-3 text-gray-400 hover:text-cyan-400 transition-colors">
                <Mail className="h-5 w-5" />
                <span>info@nilomanhx.com</span>
              </a>
              <div className="flex items-start gap-3 text-gray-400">
                <MapPin className="h-5 w-5 flex-shrink-0 mt-1" />
                <span>LGF-3, Omaxe Green valley Plaza, Gurukul Road, Faridabad-121010</span>
              </div>
            </div>
          </div>

          {/* Products */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Products</h3>
            <ul className="space-y-3">
              {productLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-cyan-400 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Company</h3>
            <ul className="space-y-3">
              {companyLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-cyan-400 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Support</h3>
            <ul className="space-y-3">
              {supportLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.path}
                    className="text-gray-400 hover:text-cyan-400 transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Copyright */}
            <p className="text-gray-400 text-sm text-center md:text-left">
              © {currentYear} Niloman HX. All rights reserved.
            </p>

            {/* Social Links */}
            <div className="flex items-center gap-4">
              <a
                href="#"
                className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gradient-to-r hover:from-cyan-500 hover:to-teal-500 transition-all duration-300 group"
              >
                <Facebook className="h-5 w-5 text-gray-400 group-hover:text-white" />
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gradient-to-r hover:from-cyan-500 hover:to-teal-500 transition-all duration-300 group"
              >
                <Twitter className="h-5 w-5 text-gray-400 group-hover:text-white" />
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gradient-to-r hover:from-cyan-500 hover:to-teal-500 transition-all duration-300 group"
              >
                <Linkedin className="h-5 w-5 text-gray-400 group-hover:text-white" />
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gradient-to-r hover:from-cyan-500 hover:to-teal-500 transition-all duration-300 group"
              >
                <Instagram className="h-5 w-5 text-gray-400 group-hover:text-white" />
              </a>
            </div>

            {/* Legal Links */}
            <div className="flex items-center gap-6 text-sm">
              <a href="#" className="text-gray-400 hover:text-cyan-400 transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-gray-400 hover:text-cyan-400 transition-colors">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
